import {
  afterNextRender,
  Component,
  DestroyRef,
  HostListener,
  inject,
  NgZone,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChatLlmComponent } from './shared/chatllm/chatllm';
import { ThemeService } from './core/services/theme.service';
import { LoaderService } from './widgets/global-loader/global-loader.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, ChatLlmComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  protected readonly title = signal('Infraon v2');

  showButton = false;
  private readonly SCROLL_THRESHOLD = 300;
  btnColor!: Record<string, string>;
  private destroyRef = inject(DestroyRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private zone = inject(NgZone);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private themeService: ThemeService,
    private loader: LoaderService,
  ) {
    this.themeService.initTheme();
    afterNextRender(() => this.trackFocusRingColour());
  }

  // CSS can't read an element's background colour into its outline, so on every focus the focused
  // element gets its own background colour as --focus-ring (used by the focus ring in styles.scss).
  // Transparent, see-through and near-white backgrounds are skipped so the ring falls back to the
  // text colour and stays visible on the page. Read again when a colour transition ends, because
  // some buttons animate their background as they gain focus. Runs outside Angular: DOM only.
  private trackFocusRingColour(): void {
    const setRing = (el: HTMLElement) => {
      const background = getComputedStyle(el).backgroundColor;
      const rgba = background.match(/[\d.]+/g)?.map(Number) ?? [];
      const [r, g, b, a = 1] = rgba;
      const visible = rgba.length >= 3 && a > 0.5 && !(r > 235 && g > 235 && b > 235);
      if (visible) el.style.setProperty('--focus-ring', `rgb(${r} ${g} ${b})`);
      else el.style.removeProperty('--focus-ring');
    };
    const onFocusIn = (event: FocusEvent) => {
      const el = event.target;
      if (!(el instanceof HTMLElement)) return;
      setRing(el);
      const onTransitionEnd = (e: TransitionEvent) => {
        if (e.target === el && e.propertyName === 'background-color') setRing(el);
      };
      el.addEventListener('transitionend', onTransitionEnd);
      el.addEventListener(
        'focusout',
        () => el.removeEventListener('transitionend', onTransitionEnd),
        {
          once: true,
        },
      );
    };
    this.zone.runOutsideAngular(() => document.addEventListener('focusin', onFocusIn));
    this.destroyRef.onDestroy(() => document.removeEventListener('focusin', onFocusIn));
  }

  ngOnInit(): void {
    this.loader.show();
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loader.show();
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loader.hide();
        this.applyFooterBanner();
      }
    });
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (!this.isBrowser) return;
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    this.showButton = scrollTop > this.SCROLL_THRESHOLD;
  }

  // Built from the current path: the site's <base href="/"> would resolve a bare "#main-content"
  // against the homepage if the link were followed before hydration.
  get skipHref(): string {
    return this.router.url.split(/[?#]/)[0] + '#main-content';
  }

  skipToMain(event: Event) {
    const main = document.getElementById('main-content');
    if (!main) return;
    event.preventDefault();
    main.focus();
    main.scrollIntoView();
  }

  scrollToTop() {
    if (!this.isBrowser) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private applyFooterBanner() {
    const data = this.getDeepestRoute(this.route)?.snapshot.data['footerBanner'];
    if (!data) return;

    this.btnColor = {
      fill: data?.color ? `var(--${data.color})` : 'var(--primary)',
    };
  }

  private getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
}
